import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  message, Modal,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Tooltip,
} from 'antd';
import * as Component from 'components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TableColumnInterface } from 'type/tableType';
import { videoSourceService } from '../service/videoSourceService';
import { BatchJobResponse, HierarchicalS3Item, S3ItemType } from '../type/videoSourceType';
import { S3_BUCKET_NAME } from 'constants/constant';

import {
  CloseOutlined,
  EditOutlined,
  FileOutlined,
  FilterOutlined,
  FolderOutlined,
  SaveOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import './assetSource.css';

const { RangePicker } = DatePicker;

export default function VideoSource(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [flattenedData, setFlattenedData] = useState<HierarchicalS3Item[]>([]);
  const [_originalData, setOriginalData] = useState<HierarchicalS3Item[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // 편집 관련 상태
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const [isMoving, setIsMoving] = useState<boolean>(false);
  const [processingKeys, setProcessingKeys] = useState<Set<string>>(new Set());

  // 검색 및 필터링 상태
  // URL에서 초기값 가져오기
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [dateRange, setDateRange] = useState<any>(null);
  const [showFilesOnly, setShowFilesOnly] = useState<boolean>(
    searchParams.get('files_only') === 'true'
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // 테이블 강제 리렌더링을 위한 상태
  const [tableKey, setTableKey] = useState(0);

  // 초기 페이지네이션 설정
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized) {
      const page = searchParams.get('page');
      const pageSize = searchParams.get('page_size');

      if (!page || !pageSize) {
        setSearchParams(prev => {
          if (!prev.get('page')) prev.set('page', '1');
          if (!prev.get('page_size')) prev.set('page_size', '100');
          return prev;
        });
      }
      setIsInitialized(true);
    }
  }, [searchParams, setSearchParams, isInitialized]);

  // 🚫 시스템 파일 및 불필요한 파일 제외 함수
  const isSystemOrHiddenFile = (fileName: string): boolean => {
    const systemFiles = [
      '.DS_Store',
      '._.DS_Store',
      'Thumbs.db',
      'Desktop.ini',
      '.directory',
      '__MACOSX',
      '.Trashes',
      '.fseventsd',
      '.Spotlight-V100',
      '.TemporaryItems',
      '.VolumeIcon.icns',
      '.AppleDouble',
      '.LSOverride',
      '.DocumentRevisions-V100',
      '.fuse_hidden',
    ];

    const fileNameLower = fileName.toLowerCase();

    // 시스템 파일 체크
    if (
      systemFiles.some(sysFile => fileName === sysFile || fileNameLower === sysFile.toLowerCase())
    ) {
      return true;
    }

    // 숨김 파일 체크 (. 으로 시작)
    if (fileName.startsWith('.') && fileName.length > 1) {
      return true;
    }

    // 임시 파일 체크
    if (fileName.startsWith('~') || fileName.endsWith('.tmp') || fileName.endsWith('.temp')) {
      return true;
    }

    return false;
  };

  // 🎬 동영상 파일 확장자 확인 함수 (확장된 목록)
  const isVideoFile = (fileName: string): boolean => {
    const videoExtensions = [
      'mp4',
      'avi',
      'mov',
      'wmv',
      'flv',
      'mkv',
      'm4v',
      'webm',
      'ogv',
      '3gp',
      '3g2',
      'f4v',
      'f4p',
      'f4a',
      'f4b',
      'ts',
      'm2ts',
      'mts',
      'vob',
      'mpg',
      'mpeg',
      'm1v',
      'm2v',
      'asf',
      'rm',
      'rmvb',
      'divx',
      'xvid',
      'mxf',
      'dv',
      'qt',
      'mod',
      'tod',
      'm4p',
      'amv',
      'mp2',
      'mpe',
      'mpv',
      'mp2v',
      'svi',
      'nsv',
      'roq',
      'yuv',
    ];

    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    return videoExtensions.includes(extension);
  };

  useEffect(() => {
    const fetchRootDirectories = async () => {
      setIsLoading(true);
      try {
        const pageSize = parseInt(searchParams.get('page_size') || '100');
        const response = await videoSourceService.fetchSubItemsPaginated(
          1, // 첫 페이지
          pageSize
        );

        const hierarchicalData = response.items
          .map((item: S3ItemType) => ({
            ...(item as HierarchicalS3Item),
            level: 0,
            expanded: false,
          }))
          .filter((item: HierarchicalS3Item) => {
            // 디렉토리는 항상 표시
            if (item.isDirectory) return true;

            // 파일의 경우 시스템 파일 제외
            const fileName = item.Key.split('/').pop() || '';
            return !isSystemOrHiddenFile(fileName);
          });

        setOriginalData(hierarchicalData);
        setFlattenedData(hierarchicalData);
      } catch {
        Modal.error({
          title: 'Error',
          content: 'Failed to fetch directories',
          okText: 'Confirm',
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchRootDirectories();
  }, []);

  // 필터링된 데이터 개수 계산 (Results Summary용)
  const getFilteredCount = useMemo(() => {
    // URL 파라미터에서 필터 값들 읽기 (테이블과 동일한 로직 사용)
    const searchFromUrl = searchParams.get('search') || '';
    const filesOnlyFromUrl = searchParams.get('files_only') === 'true';

    let filtered = [...flattenedData];

    // 1️⃣ 시스템 파일 제외 및 비디오 파일 필터링
    filtered = filtered.filter(item => {
      if (item.isDirectory) return true;
      const fileName = item.Key.split('/').pop() || '';
      if (isSystemOrHiddenFile(fileName)) return false;
      return isVideoFile(fileName);
    });

    // 2️⃣ 검색어 필터링 (URL 파라미터 사용)
    if (searchFromUrl.trim()) {
      const query = searchFromUrl.toLowerCase();
      filtered = filtered.filter(item => {
        const pathParts = item.Key.split('/').filter(part => part.length > 0);
        const fileName = pathParts[pathParts.length - 1]?.toLowerCase() || '';
        const fullPath = item.Key.toLowerCase();
        return fileName.includes(query) || fullPath.includes(query);
      });
    }

    // 3️⃣ 날짜 범위 필터링
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      filtered = filtered.filter(item => {
        if (!item.LastModified) return false;
        const itemDate = new Date(item.LastModified);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    // 4️⃣ 파일만 표시 필터링 (URL 파라미터 사용)
    if (filesOnlyFromUrl) {
      filtered = filtered.filter(item => !item.isDirectory);
    }

    return filtered.length;
  }, [flattenedData, searchParams, dateRange]); // URL 파라미터와 의존성 동기화

  // 통계 계산 (필터링된 비디오 데이터 기반)
  const statistics = useMemo(() => {
    const filteredData = flattenedData.filter(item => {
      if (item.isDirectory) return true;
      const fileName = item.Key.split('/').pop() || '';
      if (isSystemOrHiddenFile(fileName)) return false;
      return isVideoFile(fileName);
    });

    const totalItems = filteredData.length;
    const directories = filteredData.filter(item => item.isDirectory).length;
    const files = totalItems - directories;
    const totalSize = filteredData
      .filter(item => !item.isDirectory)
      .reduce((sum, item) => sum + (item.Size || 0), 0);

    return { totalItems, directories, files, totalSize };
  }, [flattenedData]);

  // 유틸리티 함수들
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // 이전 필터 값들을 저장하기 위한 ref
  const prevFiltersRef = React.useRef({
    searchQuery: '',
    dateRange: null,
    showFilesOnly: false,
  });

  // 필터가 실제로 변경될 때 URL에 동기화하고 페이지를 1로 리셋
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const filtersChanged =
      prevFilters.searchQuery !== searchQuery ||
      prevFilters.dateRange !== dateRange ||
      prevFilters.showFilesOnly !== showFilesOnly;

    if (filtersChanged) {
      setSearchParams(prev => {
        // 검색어 동기화
        if (searchQuery.trim()) {
          prev.set('search', searchQuery);
        } else {
          prev.delete('search');
        }

        // 파일 전용 필터 동기화
        if (showFilesOnly) {
          prev.set('files_only', 'true');
        } else {
          prev.delete('files_only');
        }

        // 필터가 변경되었으므로 페이지를 1로 리셋
        prev.set('page', '1');

        return prev;
      });

      // 현재 필터 값들을 저장
      prevFiltersRef.current = {
        searchQuery,
        dateRange,
        showFilesOnly,
      };
    }
  }, [searchQuery, dateRange, showFilesOnly, setSearchParams]);

  // 필터 초기화
  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setDateRange(null);
    setShowFilesOnly(false);
    setSearchParams(prev => {
      prev.set('page', '1');
      return prev;
    });
  }, [setSearchParams]);

  // 페이지네이션을 지원하는 데이터 페치 함수 (클라이언트 필터링 + 페이지네이션)
  const paginatedDataFetch = useCallback(
    async (params: URLSearchParams) => {
      const page = parseInt(params.get('page') || '1');
      const pageSize = parseInt(params.get('page_size') || '100');

      // URL 파라미터에서 필터 값들 읽기
      const searchFromUrl = params.get('search') || '';
      const filesOnlyFromUrl = params.get('files_only') === 'true';

      let filtered = [...flattenedData];

      // 1️⃣ 시스템 파일 제외 및 비디오 파일 필터링
      filtered = filtered.filter(item => {
        if (item.isDirectory) return true;
        const fileName = item.Key.split('/').pop() || '';
        if (isSystemOrHiddenFile(fileName)) return false;
        return isVideoFile(fileName);
      });

      // 2️⃣ 검색어 필터링 (URL 파라미터 사용)
      if (searchFromUrl.trim()) {
        const query = searchFromUrl.toLowerCase();
        filtered = filtered.filter(item => {
          const pathParts = item.Key.split('/').filter(part => part.length > 0);
          const fileName = pathParts[pathParts.length - 1]?.toLowerCase() || '';
          const fullPath = item.Key.toLowerCase();
          return fileName.includes(query) || fullPath.includes(query);
        });
      }

      // 3️⃣ 날짜 범위 필터링
      if (dateRange && dateRange.length === 2) {
        const [startDate, endDate] = dateRange;
        filtered = filtered.filter(item => {
          if (!item.LastModified) return false;
          const itemDate = new Date(item.LastModified);
          return itemDate >= startDate && itemDate <= endDate;
        });
      }

      // 4️⃣ 파일만 표시 필터링 (URL 파라미터 사용)
      if (filesOnlyFromUrl) {
        filtered = filtered.filter(item => !item.isDirectory);
      }

      // 5️⃣ 클라이언트 사이드 페이지네이션 적용
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filtered.slice(startIndex, endIndex);

      return {
        data: paginatedData,
        count: filtered.length,
      };
    },
    [flattenedData, dateRange] // searchQuery와 showFilesOnly는 URL에서 읽으므로 제외
  );

  // 파일명 편집 시작
  const startEditing = (record: HierarchicalS3Item) => {
    const fileName = record.Key.split('/').pop() || '';
    setEditingKey(record.Key);
    setEditingName(fileName);
  };

  // 파일명 편집 저장
  const saveEditing = async () => {
    // 중복 클릭 방지
    if (isRenaming) return;

    setIsRenaming(true);
    if (!editingKey || !editingName.trim()) {
      message.warning('Please enter a file name.');
      setIsRenaming(false);
      return;
    }

    try {
      // Calculate correct destination prefix
      const sourcePrefix = editingKey;
      const pathParts = editingKey.split('/');

      // Replace only the last part (filename) with the new name
      pathParts[pathParts.length - 1] = editingName;
      const destPrefix = pathParts.join('/');

      // Call API to rename file
      await videoSourceService.renameFile(sourcePrefix, destPrefix);

      // Update UI: replace old key with new key
      const newData = flattenedData.map(item => {
        if (item.Key === editingKey) {
          return { ...item, Key: destPrefix };
        }
        return item;
      });
      setFlattenedData(newData);
      setOriginalData(newData);

      // Force table re-render
      setTableKey(prev => prev + 1);

      message.success('File name has been changed.');
      setEditingKey(null);
      setEditingName('');
    } catch (error: any) {
      // 에러 메시지 안전하게 처리
      let errorMessage = 'Failed to rename file.';
      if (error?.response?.data?.error) {
        errorMessage = `Failed to rename file: ${error.response.data.error}`;
      } else if (error?.response?.data?.detail) {
        errorMessage = `Failed to rename file: ${error.response.data.detail}`;
      } else if (error?.message) {
        errorMessage = `Failed to rename file: ${error.message}`;
      } else if (typeof error === 'string') {
        errorMessage = `Failed to rename file: ${error}`;
      }

      // 메시지가 비어있지 않을 때만 표시
      if (errorMessage && errorMessage.trim()) {
        Modal.error({
          title: 'Error',
          content: errorMessage,
          okText: 'Confirm',
        });
      } else {
        Modal.error({
          title: 'Error',
          content: 'Failed to rename file.',
          okText: 'Confirm',
        });
      }
    } finally {
      setIsRenaming(false);
    }
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditingName('');
  };

  // Check if a directory has subdirectories (not leaf directory)
  const hasSubDirectories = useCallback(
    (record: HierarchicalS3Item): boolean => {
      if (!record.isDirectory) return false;

      const keyPrefix = record.Key.endsWith('/') ? record.Key : record.Key + '/';

      // Check if there are any subdirectories under this directory
      return flattenedData.some(item => {
        if (item.Key === record.Key) return false;
        return item.isDirectory && item.Key.startsWith(keyPrefix);
      });
    },
    [flattenedData]
  );

  const handleExpand = async (record: HierarchicalS3Item) => {
    const newExpandedKeys = new Set(expandedKeys);

    if (expandedKeys.has(record.Key)) {
      // 디렉토리를 닫을 때: 해당 디렉토리와 모든 하위 디렉토리의 확장 상태 제거
      newExpandedKeys.delete(record.Key);

      // 해당 디렉토리 하위의 모든 항목 제거 및 확장 상태 정리
      const keyPrefix = record.Key.endsWith('/') ? record.Key : record.Key + '/';

      // 제거될 하위 항목들을 먼저 찾기 (현재 flattenedData에서)
      const itemsToRemove = flattenedData.filter(item => {
        if (item.Key === record.Key) return false;

        // 더 정확한 패턴 매칭: keyPrefix로 시작하거나 record.Key/로 시작하는 경우
        return (
          item.Key.startsWith(keyPrefix) ||
          (item.Key.startsWith(record.Key) && item.Key.includes('/'))
        );
      });

      // 제거될 항목들의 확장 상태를 모두 제거
      itemsToRemove.forEach(item => {
        newExpandedKeys.delete(item.Key);
      });

      // expandedKeys에서 패턴 기반으로 추가 제거 (더 안전한 정리)
      const keysToRemove: string[] = [];
      expandedKeys.forEach(key => {
        if (
          key.startsWith(keyPrefix) ||
          (key.startsWith(record.Key) && key.includes('/') && key !== record.Key)
        ) {
          keysToRemove.push(key);
        }
      });

      keysToRemove.forEach(key => newExpandedKeys.delete(key));

      // flattenedData에서 하위 항목들 제거
      const newData = flattenedData.filter(item => {
        if (item.Key === record.Key) return true;
        return !item.Key.startsWith(keyPrefix);
      });
      setFlattenedData(newData);
      setTableKey(prev => prev + 1); // 테이블 강제 리렌더링
    } else {
      // 디렉토리를 열 때: 중복 체크 후 직접 하위 항목들만 로드
      newExpandedKeys.add(record.Key);

      try {
        // 이미 하위 항목이 로드되어 있는지 확인
        const keyPrefix = record.Key.endsWith('/') ? record.Key : record.Key + '/';
        const hasChildren = flattenedData.some(
          item => item.Key.startsWith(keyPrefix) && item.Key !== record.Key
        );

        if (!hasChildren) {
          const response = await videoSourceService.fetchSubItemsPaginated(1, 1000, record.Key);

          const hierarchicalChildren = response.items
            .filter((child: S3ItemType) => {
              if (child.isDirectory) return true;

              const fileName = child.Key.split('/').pop() || '';
              return !isSystemOrHiddenFile(fileName);
            })
            .map((child: S3ItemType) => ({
              ...(child as HierarchicalS3Item),
              level: record.level + 1,
              expanded: false,
            }));

          hierarchicalChildren.forEach(child => {
            if (child.isDirectory) {
              newExpandedKeys.has(child.Key);
              newExpandedKeys.delete(child.Key);
            }
          });

          const recordIndex = flattenedData.findIndex(item => item.Key === record.Key);
          const newData = [
            ...flattenedData.slice(0, recordIndex + 1),
            ...hierarchicalChildren,
            ...flattenedData.slice(recordIndex + 1),
          ];
          setFlattenedData(newData);
          setTableKey(prev => prev + 1); // 테이블 강제 리렌더링
        }
      } catch (error) {
        console.error('Error fetching sub items:', error);
        Modal.error({
          title: 'Error',
          content: 'Failed to load directory contents',
          okText: 'Confirm',
        });
        newExpandedKeys.delete(record.Key);
      }
    }

    setExpandedKeys(newExpandedKeys);
  };

  const columns: TableColumnInterface<HierarchicalS3Item>[] = [
    {
      title: 'Name',
      dataIndex: 'Key',
      key: 'name',
      render: (key: string, record: HierarchicalS3Item) => {
        const name = record.isDirectory
          ? key.split('/').filter(Boolean).pop() + '/'
          : key.split('/').pop();

        const indentation = record.level * 24; // 24px per level
        const isExpanded = expandedKeys.has(record.Key);
        const isEditing = editingKey === record.Key;

        return (
          <div style={{ paddingLeft: indentation, display: 'flex', alignItems: 'center' }}>
            {record.isDirectory && (
              <span
                onClick={() => handleExpand(record)}
                style={{
                  cursor: 'pointer',
                  marginRight: '8px',
                  fontSize: '12px',
                  color: '#666',
                  width: '12px',
                  display: 'inline-block',
                }}
              >
                {isExpanded ? '▼' : '▶'}
              </span>
            )}
            {!record.isDirectory && <span style={{ width: '20px' }}></span>}

            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <span style={{ marginRight: '8px' }}>{record.isDirectory ? '📁' : '📄'}</span>

              {record.isDirectory ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span
                    style={{
                      color: '#1890ff',
                      fontWeight: '500',
                    }}
                  >
                    {name}
                  </span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  {isEditing ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <Input
                        size="small"
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onPressEnter={saveEditing}
                        style={{ flex: 1, maxWidth: '300px' }}
                      />
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <Tooltip title="Save">
                          <Button
                            type="text"
                            size="small"
                            icon={<SaveOutlined />}
                            onClick={saveEditing}
                            style={{ color: '#52c41a' }}
                          />
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={cancelEditing}
                            style={{ color: '#f5222d' }}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flex: 1,
                        cursor: 'pointer',
                      }}
                      onClick={() => startEditing(record)}
                    >
                      <span style={{ flex: 1 }}>{name}</span>
                      <Tooltip title="Edit Name">
                        <EditOutlined
                          style={{
                            color: '#1890ff',
                            opacity: 0.7,
                            fontSize: '12px',
                          }}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'isDirectory',
      key: 'type',
      width: '10%',
      render: (_: any, record: HierarchicalS3Item) => (
        <span style={{ color: record.isDirectory ? '#1890ff' : '#52c41a' }}>
          {record.isDirectory ? 'Directory' : 'File'}
        </span>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'Size',
      key: 'size',
      width: '15%',
      render: (size: any, record: HierarchicalS3Item) => {
        if (record.isDirectory) return '-';
        return formatFileSize(size || 0);
      },
    },
    {
      title: 'Last Modified',
      dataIndex: 'LastModified',
      key: 'lastModified',
      width: '20%',
      render: (date: string, record: HierarchicalS3Item) => {
        if (record.isDirectory) return '-';
        if (!date) return '-';
        return new Date(date).toLocaleString();
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: '15%',
      render: (_: any, record: HierarchicalS3Item) => {
        // Hide register button for directories that have subdirectories
        if (
          (record.isDirectory && hasSubDirectories(record)) ||
          (record.isDirectory && !expandedKeys.has(record.Key))
        ) {
          return (
            <div>
              <span style={{ color: '#999', fontSize: '12px' }}></span>
            </div>
          );
        }

        const recordKey = record.Key;
        const isProcessing = processingKeys.has(recordKey);

        return (
          <div>
            <Button
              size="small"
              type="link"
              disabled={isProcessing || isMoving}
              loading={isProcessing}
              style={{ color: '#1890ff', fontWeight: '500', padding: 0 }}
              onClick={() => {
                // 중복 클릭 방지 - 해당 키가 이미 처리 중이면 리턴
                if (isProcessing || processingKeys.has(recordKey)) return;

                // 처리 중인 키 추가
                setProcessingKeys(prev => new Set(prev).add(recordKey));
                setIsMoving(true);

                videoSourceService
                  .moveAssetSource(record)
                  .then((response: BatchJobResponse) => {
                    const passedFiles = response.passed_files || [];
                    const passedCount = Array.isArray(passedFiles) ? passedFiles.length : 0;
                    const successMessage = response.message || 'Ingest completed successfully';
                    message.success(`${successMessage}, passed ${passedCount} files`);
                    setProcessingKeys(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(recordKey);
                      return newSet;
                    });
                    setIsMoving(false);
                  })
                  .catch((error: any) => {
                    // 에러 메시지 안전하게 처리
                    let errorMessage = 'Failed to ingest asset source';
                    if (error?.response?.data?.error) {
                      errorMessage = error.response.data.error;
                    } else if (error?.response?.data?.detail) {
                      errorMessage = error.response.data.detail;
                    } else if (error?.message) {
                      errorMessage = error.message;
                    } else if (typeof error === 'string') {
                      errorMessage = error;
                    }

                    // 메시지가 비어있지 않을 때만 표시
                    if (errorMessage && errorMessage.trim()) {
                      Modal.error({
                        title: 'Error',
                        content: errorMessage,
                        okText: 'Confirm',
                      });
                    } else {
                      Modal.error({
                        title: 'Error',
                        content: 'Failed to ingest asset source',
                        okText: 'Confirm',
                      });
                    }

                    setProcessingKeys(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(recordKey);
                      return newSet;
                    });
                    setIsMoving(false);
                  });
              }}
            >
              {record.isDirectory ? 'Ingest Folder' : 'Ingest File'}
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <Component.BasePageComponent>
      <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Header with Title and Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Card>
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  <Space direction="vertical" size="small">
                    <h2 style={{ margin: 0, color: '#1890ff' }}>
                      <FolderOutlined /> S3 Video Source Manager
                    </h2>
                    <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '18px' }}>
                      Root Directory Path : {S3_BUCKET_NAME}/backedup/
                      <br />
                    </p>
                  </Space>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Loaded Items"
                value={statistics.totalItems}
                prefix={<FileOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Directories"
                value={statistics.directories}
                prefix={<FolderOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Video Files"
                value={statistics.files}
                prefix={<FileOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Size"
                value={formatFileSize(statistics.totalSize)}
                prefix={<FileOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Search and Filter Controls */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Card>
              <Row gutter={[16, 16]}>
                {/* Search Input */}
                <Col xs={24} sm={12} md={8}>
                  <Input
                    placeholder="Search video files and folders..."
                    prefix={<SearchOutlined />}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    allowClear
                  />
                </Col>

                {/* Date Range Filter */}
                <Col xs={24} sm={12} md={8}>
                  <RangePicker
                    value={dateRange}
                    onChange={dates => setDateRange(dates)}
                    style={{ width: '100%' }}
                    placeholder={['Start Date', 'End Date']}
                  />
                </Col>

                {/* Filter Actions */}
                <Col xs={24} sm={12} md={8}>
                  <Space>
                    <Button
                      icon={<FilterOutlined />}
                      onClick={() => setShowFilesOnly(!showFilesOnly)}
                      type={showFilesOnly ? 'primary' : 'default'}
                      size="small"
                    >
                      Videos Only
                    </Button>
                    <Button onClick={resetFilters} size="small">
                      Reset
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Results Summary */}
        {(searchParams.get('search') || dateRange || searchParams.get('files_only') === 'true') && (
          <Row style={{ marginBottom: '16px' }}>
            <Col span={24}>
              <div
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#e6f7ff',
                  borderRadius: '4px',
                  border: '1px solid #91d5ff',
                }}
              >
                <Space wrap>
                  <span>
                    <strong>Showing {getFilteredCount} video items</strong>
                  </span>
                  {searchParams.get('search') && (
                    <Tag closable onClose={() => setSearchQuery('')}>
                      Search: &ldquo;{searchParams.get('search')}&rdquo;
                    </Tag>
                  )}
                  {dateRange && (
                    <Tag closable onClose={() => setDateRange(null)}>
                      Date Range
                    </Tag>
                  )}
                  {searchParams.get('files_only') === 'true' && (
                    <Tag closable onClose={() => setShowFilesOnly(false)}>
                      Videos Only
                    </Tag>
                  )}
                </Space>
              </div>
            </Col>
          </Row>
        )}

        {/* Main Table */}
        {isLoading ? (
          <Component.LoadingSpinnerComponent />
        ) : (
          <Row>
            <Col span={24}>
              <Card>
                <Component.TableComponent<HierarchicalS3Item>
                  key={`table-${tableKey}`} // 데이터 변경 시 강제 리렌더링
                  name=""
                  dataFetch={paginatedDataFetch}
                  columns={columns}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  actions={{}}
                  options={{
                    isDisableIdColumn: true,
                  }}
                />
              </Card>
            </Col>
          </Row>
        )}
      </div>

      {/* Loading Overlays */}
      {isRenaming && (
        <Spin
          tip="Renaming file..."
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
          }}
        >
          <div className="content" />
        </Spin>
      )}
      {isMoving && (
        <Spin
          tip="Ingesting file..."
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
          }}
        >
          <div className="content" />
        </Spin>
      )}
    </Component.BasePageComponent>
  );
}
